import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { authApi } from '@/api/auth'
import { Alert, AuthLayout, Button, Field } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { useAuthStore } from '@/stores/authStore'

/** Ίδιο ελάχιστο μήκος με τον έλεγχο του backend, ώστε να μην υπάρξει απόκλιση. */
const MIN_PASSWORD_LENGTH = 10

const schema = z.object({
  organizationName: z.string().min(2),
  fullName: z.string().min(2),
  email: z.string().email(),
  phone: z.string().max(32).optional().or(z.literal('')),
  vatNumber: z.string().max(20).optional().or(z.literal('')),
  password: z.string().min(MIN_PASSWORD_LENGTH),
})

type FormValues = z.infer<typeof schema>

export default function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const translateError = useApiError()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) })

  const mutation = useMutation({
    mutationFn: authApi.register,
    onSuccess: (session) => {
      setSession(session)
      navigate('/', { replace: true })
    },
  })

  return (
    <AuthLayout
      title={t('auth.register.title')}
      subtitle={t('auth.register.subtitle')}
      footer={
        <>
          {t('auth.register.hasAccount')}{' '}
          <Link to="/login" className="font-semibold text-brand-700 underline">
            {t('auth.register.loginLink')}
          </Link>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit((values) =>
          mutation.mutate({
            ...values,
            phone: values.phone || undefined,
            vatNumber: values.vatNumber || undefined,
          }),
        )}
        noValidate
      >
        {mutation.isError && <Alert>{translateError(mutation.error)}</Alert>}

        <Field
          label={t('auth.fields.organizationName')}
          autoComplete="organization"
          autoFocus
          error={errors.organizationName && t('auth.validation.organizationName')}
          {...register('organizationName')}
        />

        <Field
          label={t('auth.fields.vatNumber')}
          inputMode="numeric"
          error={errors.vatNumber && t('auth.validation.vatNumber')}
          {...register('vatNumber')}
        />

        <Field
          label={t('auth.fields.fullName')}
          autoComplete="name"
          error={errors.fullName && t('auth.validation.fullName')}
          {...register('fullName')}
        />

        <Field
          label={t('auth.fields.email')}
          type="email"
          autoComplete="email"
          inputMode="email"
          error={errors.email && t('auth.validation.email')}
          {...register('email')}
        />

        <Field
          label={t('auth.fields.phone')}
          type="tel"
          autoComplete="tel"
          inputMode="tel"
          {...register('phone')}
        />

        <Field
          label={t('auth.fields.password')}
          type="password"
          autoComplete="new-password"
          hint={t('auth.validation.passwordHint', { count: MIN_PASSWORD_LENGTH })}
          error={errors.password && t('auth.validation.passwordHint', { count: MIN_PASSWORD_LENGTH })}
          {...register('password')}
        />

        <Button type="submit" loading={mutation.isPending}>
          {t('auth.register.submit')}
        </Button>
      </form>
    </AuthLayout>
  )
}
